import { day } from '@vrplatform/utils';
import {
  Box,
  Group,
  Popover,
  SegmentedControl,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import {
  DatePicker,
  type DateValue,
  type DatesRangeValue,
  MonthPicker,
} from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import {
  type ComponentProps,
  type PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { Divider } from '../Divider';
import { Button } from '../button';
import { FilterPill } from './FilterPill';

export type CalendarFilterDateType = DatesRangeValue<DateValue>;
export type CheckInOutType = 'all' | 'checkIn' | 'checkOut' | 'bookedAt';

type CheckInOutTypeHandler = (value: CheckInOutType) => void;

type DateInputProps = {
  dateType?: CheckInOutType;
  setDateType?: CheckInOutTypeHandler;
  filterValue?: CalendarFilterDateType;
  setFilterValue: (value: CalendarFilterDateType | undefined) => void;
};

type Props = {
  value: DateInputProps['filterValue'] | string;
  setValue: DateInputProps['setFilterValue'];
  dateType?: DateInputProps['dateType'];
  submitType?: 'save-button' | 'instant';
  setDateType?: DateInputProps['setDateType'];
  type?: 'range' | 'single';
  clearable?: boolean;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
  width?: number;
  filterType?: ComponentProps<typeof FilterPill>['type'];
  showTodayButton?: boolean;
  numberOfColumns?: number;
  labelFormatter?: (value: DateInputProps['filterValue']) => string;
  options?: {
    label: string;
    value: CalendarFilterDateType;
  }[];
};

const getFormatted = (
  [first, second]: CalendarFilterDateType,
  labelFormatter: Props['labelFormatter']
) => {
  if (first && second) {
    const value = [
      day(first).toDate(),
      day(second).toDate(),
    ] as CalendarFilterDateType;

    return {
      label:
        labelFormatter?.(value) ??
        `${day(first).format('MMM DD')} - ${day(second).format('MMM DD YYYY')}`,
      value,
    };
  }
  if (first) {
    const value = [day(first).toDate(), null] as CalendarFilterDateType;

    return {
      label: labelFormatter?.(value) ?? `${day(first).format('MMM DD YYYY')}`,
      value,
    };
  }

  return { label: labelFormatter?.(undefined) ?? '', value: undefined };
};

const getInclusiveLastDaysStart = (days: number, end = day()) =>
  end.subtract(days, 'days').add(1, 'day');

const getInclusiveLast12MonthsStart = (end = day()) =>
  end.subtract(12, 'months').add(1, 'day');

export const DateFilter = (props: Props) => {
  'use-memo';

  const { value: v, setValue, setDateType, clearable = true } = props;
  const [opened, handlers] = useDisclosure(false);

  const { value, label } = useMemo<{
    value: CalendarFilterDateType | undefined;
    label: string;
  }>(() => {
    if (Array.isArray(v)) return getFormatted(v, props.labelFormatter);

    if (typeof v === 'string') {
      const [first, second] = v.split('...');

      return getFormatted([first, second], props.labelFormatter);
    }
    return { value: undefined, label: '' };
  }, [v, props.labelFormatter]);

  const activeOptions = useActiveDateRange(value);

  const activeOptionKey = Object.entries(activeOptions).find(
    ([, value]) => value
  )?.[0];

  return (
    <Popover
      position="bottom-start"
      shadow="md"
      withinPortal
      opened={opened}
      onChange={handlers.toggle}
    >
      <Popover.Target>
        <FilterPill
          aria-label={props.placeholder || label || 'Date filter'}
          opened={opened}
          isDefault={!value}
          type={props.filterType}
          width={props.width}
          label={
            getSelectOptionLabel(activeOptionKey as keyof ActiveElements) ||
            label ||
            props.placeholder ||
            'Date'
          }
          onClick={handlers.toggle}
          resetFilter={
            !clearable
              ? undefined
              : () => {
                  setValue(undefined);
                  handlers.close();
                  if (setDateType) {
                    setDateType('all');
                  }
                }
          }
        />
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <Dropdown {...props} value={value} closeModal={handlers.close} />
      </Popover.Dropdown>
    </Popover>
  );
};

const Dropdown = ({
  value: initial,
  setValue: setInitial,
  dateType: dT,
  setDateType: setDT,
  closeModal,
  options,
  type = 'range',
  minDate,
  submitType = 'save-button',
  numberOfColumns = 2,
  maxDate,
  showTodayButton = true,
}: Omit<Props, 'value'> & {
  value: CalendarFilterDateType | undefined;
  closeModal: () => void;
}) => {
  'use-memo';

  const theme = useMantineTheme();
  const [value, setV] = useState<CalendarFilterDateType | undefined>(initial);

  const [dateType, setDt] = useState(dT || 'all');

  const [isMonthPicker, setIsMonthPicker] = useState(false);

  const { colorScheme } = useMantineColorScheme();

  const borderColor = theme.colors.gray[colorScheme === 'dark' ? 8 : 3];

  const onSubmit = useCallback(
    (optionalValue?: typeof value, optionalDateType?: typeof dateType) => {
      const temp = optionalValue ?? value;

      const hasValue = temp && (temp[0] || temp[1]);
      setInitial(
        hasValue
          ? (() => {
              if (isMonthPicker) {
                return [
                  temp[0],
                  temp[1]
                    ? day(temp[1]).endOf('month').toDate()
                    : day(temp[0]).endOf('month').toDate(),
                ];
              }
              return temp;
            })()
          : undefined
      );
      if (hasValue && setDT) setDT(optionalDateType ?? dateType);
      closeModal();
    },
    [isMonthPicker, dateType, setInitial, setDT, closeModal, value]
  );

  const setValue = useCallback(
    (v: CalendarFilterDateType | undefined) => {
      setV(v);
      if (submitType === 'instant') {
        onSubmit(v);
      }
    },
    [submitType, onSubmit]
  );

  const setDateType = useCallback(
    (v: typeof dateType) => {
      setDt(v);
      if (submitType === 'instant') {
        onSubmit(value);
      }
    },
    [submitType, onSubmit, value]
  );

  const activeElements = useActiveDateRange(value);

  const setCurrentYear = useCallback(() => {
    const lastYear = day();
    setValue([
      lastYear.startOf('year').toDate(),
      lastYear.endOf('year').toDate(),
    ]);
    setIsMonthPicker(true);
  }, [setValue]);

  const setLastYear = useCallback(() => {
    const lastYear = day().subtract(1, 'year');
    setValue([
      lastYear.startOf('year').toDate(),
      lastYear.endOf('year').toDate(),
    ]);
    setIsMonthPicker(true);
  }, [setValue]);

  const setLast12Months = useCallback(() => {
    const today = day();
    setValue([getInclusiveLast12MonthsStart(today).toDate(), today.toDate()]);
    setIsMonthPicker(false);
  }, [setValue]);

  const setLast30Days = useCallback(() => {
    const today = day();
    setValue([getInclusiveLastDaysStart(30, today).toDate(), today.toDate()]);
    setIsMonthPicker(false);
  }, [setValue]);

  const setLast7Days = useCallback(() => {
    const today = day();
    setValue([getInclusiveLastDaysStart(7, today).toDate(), today.toDate()]);
    setIsMonthPicker(false);
  }, [setValue]);

  const isShowTodayButton = useMemo(() => {
    return type === 'single' && showTodayButton;
  }, [type, showTodayButton]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'nowrap',
      }}
    >
      {type === 'range' && (
        <Box
          sx={{
            width: '175px',
            borderRight: '1px solid',
            borderColor,
            display: 'flex',
            flexDirection: 'column',
          }}
          p={5}
        >
          <OptionButton
            onClick={setLast7Days}
            isActive={activeElements.last7Days}
          >
            {getSelectOptionLabel('last7Days')}
          </OptionButton>
          <OptionButton
            onClick={setLast30Days}
            isActive={activeElements.last30Days}
          >
            {getSelectOptionLabel('last30Days')}
          </OptionButton>
          <OptionButton
            onClick={setLast12Months}
            isActive={activeElements.last12Months}
          >
            {getSelectOptionLabel('last12Months')}
          </OptionButton>

          <Divider my={5} mx={-5} />

          {options?.map((option) => (
            <OptionButton
              key={option.label}
              onClick={() => setValue(option.value)}
              isActive={
                option.value.map((v) => day(v).yyyymmdd()).join('...') ===
                value?.map((v) => day(v).yyyymmdd()).join('...')
              }
            >
              {option.label}
            </OptionButton>
          ))}
          <OptionButton
            onClick={setCurrentYear}
            isActive={activeElements.currentYear}
          >
            {getSelectOptionLabel('currentYear')}
          </OptionButton>
          <OptionButton
            onClick={setLastYear}
            isActive={activeElements.lastYear}
          >
            {getSelectOptionLabel('lastYear')}
          </OptionButton>
          <OptionButton onClick={() => setIsMonthPicker(false)}>
            Custom dates
          </OptionButton>
          <OptionButton onClick={() => setIsMonthPicker(true)}>
            Custom months
          </OptionButton>
        </Box>
      )}
      <Box>
        <Box
          p="sm"
          sx={{
            borderBottom: '1px solid',
            borderColor,
          }}
        >
          {isMonthPicker ? (
            <MonthPicker
              type="range"
              numberOfColumns={numberOfColumns}
              value={value || [null, null]}
              level="year"
              nextLabel="Next period"
              previousLabel="Previous period"
              defaultDate={value?.[1] || value?.[0] || undefined} // date is for which page is currently active
              onChange={(v) => {
                setValue([
                  v[0],
                  v[1] ? day(v[1]).endOf('month').toDate() : null,
                ]);
              }}
            />
          ) : (
            <DatePicker
              value={value || [null, null]}
              type={type === 'range' ? 'range' : 'default'}
              defaultDate={value?.[1] || value?.[0] || undefined} // date is for which page is currently active
              level="month"
              numberOfColumns={numberOfColumns}
              minDate={minDate}
              maxDate={maxDate}
              nextLabel="Next period"
              previousLabel="Previous period"
              highlightToday
              onChange={(v) => {
                if (typeof v === 'string' || v instanceof Date) {
                  setValue([v, null]);
                  return;
                }

                if (Array.isArray(v)) {
                  setValue(v);
                }
              }}
            />
          )}
        </Box>
        {(isShowTodayButton || setDT || submitType === 'save-button') && (
          <Group justify="flex-end" py="sm" px="sm">
            {isShowTodayButton && (
              <Button
                size="xs"
                onClick={() => {
                  onSubmit([day().toDate(), null]);
                }}
                leftIcon="CalendarEventIcon"
                sx={{
                  marginRight: 'auto',
                }}
              >
                Today
              </Button>
            )}

            {setDT && (
              <SegmentedControl
                size="xs"
                radius="md"
                data={date_type_options}
                value={dateType}
                onChange={(value) => setDateType(value as CheckInOutType)}
                sx={{
                  justifySelf: 'flex-start',
                  marginRight: 'auto',
                }}
              />
            )}
            {submitType === 'save-button' && (
              <>
                <Button size="xs" onClick={closeModal}>
                  Cancel
                </Button>
                <Button size="xs" variant="primary" onClick={() => onSubmit()}>
                  Apply
                </Button>
              </>
            )}
          </Group>
        )}
      </Box>
    </Box>
  );
};

const date_type_options = [
  { label: 'Within', value: 'all' },
  { label: 'Check In', value: 'checkIn' },
  { label: 'Check Out', value: 'checkOut' },
  { label: 'Booked', value: 'bookedAt' },
  { label: 'Cancelled', value: 'cancelledAt' },
];

const getSelectOptionLabel = (key: keyof ActiveElements | undefined) => {
  if (!key) return null;

  const currentYear = day().year();

  const labels: Record<keyof ActiveElements, string> = {
    last7Days: 'Last 7 days',
    last30Days: 'Last 30 days',
    last12Months: 'Last 12 months',
    currentYear: currentYear.toString(),
    lastYear: `${currentYear - 1}`,
  };

  return labels[key];
};

const OptionButton = ({
  children,
  onClick,
  isActive,
}: PropsWithChildren<{
  isActive?: boolean;
  onClick?: () => void;
}>) => {
  'use-memo';

  return (
    <Button
      onClick={onClick}
      variant="light"
      size="sm"
      sx={(theme) => {
        const activeColor = theme.colors[theme.primaryColor];
        const activeBackgroundColor = activeColor[0] + 90;

        return {
          paddingInline: 9,
          '.mantine-Button-inner': {
            textAlign: 'left',
            justifyContent: 'flex-start',
          },
          '.mantine-Button-label': {
            color: isActive ? activeColor[6] : undefined,
          },
          backgroundColor: isActive ? activeBackgroundColor : undefined,
          ':hover': {
            backgroundColor: activeBackgroundColor,
          },
        };
      }}
    >
      {children}
    </Button>
  );
};

type ActiveElements = {
  last7Days: boolean;
  last30Days: boolean;
  last12Months: boolean;
  currentYear: boolean;
  lastYear: boolean;
};

function useActiveDateRange(date: CalendarFilterDateType | undefined) {
  'use-memo';

  return useMemo<ActiveElements>(() => {
    const activeElements: ActiveElements = {
      last7Days: false,
      last30Days: false,
      last12Months: false,
      currentYear: false,
      lastYear: false,
    };

    if (!date?.[0] || !date?.[1]) {
      return activeElements;
    }

    const start = day(date[0]);
    const end = day(date[1]);
    const today = day();

    const isEndToday = end.isSame(today, 'day');

    if (
      start.isSame(getInclusiveLastDaysStart(7, today), 'day') &&
      isEndToday
    ) {
      activeElements.last7Days = true;
      return activeElements;
    }

    if (
      start.isSame(getInclusiveLastDaysStart(30, today), 'day') &&
      isEndToday
    ) {
      activeElements.last30Days = true;
      return activeElements;
    }

    if (
      start.isSame(getInclusiveLast12MonthsStart(today), 'day') &&
      isEndToday
    ) {
      activeElements.last12Months = true;
      return activeElements;
    }

    if (
      start.isSame(day().startOf('year'), 'day') &&
      end.isSame(day().endOf('year'), 'day')
    ) {
      activeElements.currentYear = true;
      return activeElements;
    }

    if (
      start.isSame(day().subtract(1, 'year').startOf('year'), 'day') &&
      end.isSame(day().subtract(1, 'year').endOf('year'), 'day')
    ) {
      activeElements.lastYear = true;
      return activeElements;
    }

    return activeElements;
  }, [date]);
}
